import React, { useState } from 'react';
import { useAsync } from 'react-use';
import { always, isEmpty } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { useRouter } from 'next/router';

import {
  useFetchFavoriteArtworks,
  useFetchFavoriteTradeArtworks,
  useFetchFavoriteGroupingArtworks,
} from '@hooks/useArtworks';
import { FILTER_OPTIONS } from '@modules/groupingArtworks';
import reportError from '@utils/reportError';
import { SmallArtworkList } from '@components/smallArtworkList';
import { useArtworksRoute } from '@hooks/useArtworks/useArtworks';
import { getCurrentPLP } from '@modules/artworks/artworks.helpers';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { formMessages } from '@components/form';

import { Container, Content, Information } from './favoriteDropdown.styles';

export interface FavoriteDropdownProps {
  isOpen: boolean;
}

export const FAVOURITE_DROPDOWN_TEST_ID = 'favourite-dropdown-test-id';

export const FavoriteDropdown = ({ isOpen }: FavoriteDropdownProps) => {
  const { favoriteArtworks, fetchFavoriteArtworks } = useFetchFavoriteArtworks();
  const { favoriteTradeArtworks, fetchFavoriteTradeArtworks } = useFetchFavoriteTradeArtworks() || {};
  const router = useRouter();
  const { isGroupingRoute, isTradeMemberRoute } = useArtworksRoute();
  const fetch = isTradeMemberRoute ? fetchFavoriteTradeArtworks : fetchFavoriteArtworks;
  const { favoriteGroupingArtworks, fetchFavoriteGroupingArtworks } = useFetchFavoriteGroupingArtworks() || {};
  const currentPLP = getCurrentPLP();

  const allFavoriteArtworks = {
    groupings: favoriteGroupingArtworks,
    trade: favoriteTradeArtworks,
    artworks: favoriteArtworks,
  };

  const favArtworks = allFavoriteArtworks[currentPLP];
  const groupingSlug = router.query.grouping as string;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const hasArtworks = !isEmpty(favArtworks);

  useAsync(async () => {
    try {
      setError(false);
      setLoading(true);
      await fetch();
      if (isGroupingRoute) {
        await fetchFavoriteGroupingArtworks({
          page: 0,
          filterOptions: { filter: FILTER_OPTIONS.FAVORITES },
          limit: 7,
          slug: groupingSlug,
        });
      } else {
        await fetch();
      }
    } catch (e) {
      reportError(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  });

  const renderInformation = renderWhenTrueOtherwise(
    always(<FormattedMessage {...formMessages.generalError} />),
    always(
      <FormattedMessage
        id="favoriteDropdown.noArtworks"
        defaultMessage="You currently have no artworks in your favorites."
      />
    )
  );

  const renderArtworks = renderWhenTrueOtherwise(
    always(<SmallArtworkList artworks={favArtworks || []} />),
    always(<Information>{renderInformation(error)}</Information>)
  );
  const renderContent = renderWhenTrue(always(renderArtworks(hasArtworks)));

  return (
    <Container isOpen={isOpen} data-testid={FAVOURITE_DROPDOWN_TEST_ID}>
      <Content>{renderContent(!loading)}</Content>
    </Container>
  );
};
